import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthApi from "../../apis/auth.api";
import UserApi from "../../apis/user.api";
import AppAlert from "../../components/AppAlert";
import OutlineSmallBtn from "../../components/buttons/OutlineSmallBtn";
import SolidBlueBtn from "../../components/buttons/SolidBlueBtn";
import InputCode from "../../components/controls/InputCode";
import Spinner from "../../components/spinner/Spinner";
import TopPageBack from "../../components/TopPageBack";
import { ROUTE_PATHS } from "../../constants/Router";
import AppUtils from "../../utils";

const VerifyCode = function () {
  const CODE_LENGTH = 6;
  const codeRef = useRef("");
  const [isWarning, setIsWarning] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [openIncorrect, setOpenIncorrect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const timeRemaining = 3; //Minutes
  const location = useLocation();
  const state = location.state;
  const [timeLeft, setTimeLeft] = useState({
    minutes: 0,
    seconds: 0,
  });
  const futureTimeRef = useRef(moment().add(timeRemaining, "minutes"));
  const navigate = useNavigate();

  const inputCodeOnChange = (value) => {
    codeRef.current = value;
  };

  const onSendCodeChangePhoneNumber = async (shopIdx, phone) => {
    try {
      setResendLoading(true);
      await UserApi.sendOtp({
        shopIdx,
        phone,
      });
    } finally {
      setResendLoading(false);
    }
  };

  const onSendCodeForgetId = async () => {
    const submitData = state?.data;
    try {
      setResendLoading(true);
      await AuthApi.forgetId(submitData);
    } finally {
      setResendLoading(false);
    }
  };

  const onSendCodeForgetPassword = async () => {
    const submitData = state?.data;
    try {
      setResendLoading(true);
      await AuthApi.forgetPassword(submitData);
    } finally {
      setResendLoading(false);
    }
  };

  const calculateTimeLeft = () => {
    const currentTime = moment();
    const difference = futureTimeRef.current.diff(currentTime);

    let timeLeft = {
      minutes: 0,
      seconds: 0,
    };

    if (difference > 0) {
      timeLeft = {
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };

      if (timeLeft.minutes === 0 && timeLeft.seconds <= 10) {
        setIsWarning(true);
      } else {
        setIsWarning(false);
      }

      if (timeLeft.minutes === 0 && timeLeft.seconds === 0) {
        setIsEnded(true);
      } else {
        setIsEnded(false);
      }
    }

    return timeLeft;
  };

  const resendCode = async () => {
    const shopIdx = state?.shopIdx;
    const dashedPhoneNumber = AppUtils.replaceDash(state?.phoneNumber);

    if (state.type === "find-id") {
      onSendCodeForgetId();
    }
    if (state.type === "find-password") {
      onSendCodeForgetPassword();
    }
    if (state.type === "edit-phone-number") {
      onSendCodeChangePhoneNumber(shopIdx, dashedPhoneNumber);
    }
    if (state.type === "representative") {
      onSendCodeChangePhoneNumber(shopIdx, dashedPhoneNumber);
    }

    futureTimeRef.current = moment().add(timeRemaining, "minutes");
  };

  const onSubmit = async () => {
    if (state.type === "find-id") {
      try {
        setLoading(true);
        const { data } = await AuthApi.verifyOtpId({
          otp: codeRef.current,
          shopIdx: state.shopIdx,
        });
        if (!data.data.id) {
          setOpenIncorrect(true);
        } else {
          navigate(ROUTE_PATHS.FindIdResult, {
            state: {
              userId: data.data.id,
            },
          });
        }
      } finally {
        setLoading(false);
      }
    }
    if (state.type === "find-password") {
      try {
        setLoading(true);
        const { data } = await AuthApi.verifyOtpPassword({
          otp: codeRef.current,
          id: state?.data.id,
        });

        if (!data.data.id) {
          setOpenIncorrect(true);
        } else {
          navigate(ROUTE_PATHS.ChangePassword, {
            state: {
              otp: codeRef.current,
              id: state?.data.id
            },
          });
        }
      } finally {
        setLoading(false);
      }
    }
    if (state.type === "edit-phone-number") {
      const dashedPhoneNumber = AppUtils.replaceDash(state.phoneNumber);
      try {
        setLoading(true);
        const { data } = await UserApi.verifyChangePhoneOtp({
          shopIdx: state?.shopIdx,
          phone: dashedPhoneNumber,
          otp: codeRef.current,
        });

        if (data.data === 0) {
          setOpenIncorrect(true);
        } else {
          navigate(ROUTE_PATHS.EditInfo, {
            state: {
              success: true,
            },
          });
        }
      } catch (error) {
        setOpenIncorrect(true);
      } finally {
        setLoading(false);
      }
    }
    if (state.type === "representative") {
      navigate(ROUTE_PATHS.DeleteReason, {
        state: {
          otp: codeRef.current,
          type: "delete-account",
        },
      });
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);
  const onBack = ()=>{
    navigate(ROUTE_PATHS.More)
  }
  return (
    <div>
      <TopPageBack onBack ={onBack}>
        <b>인증번호 입력</b>
      </TopPageBack>
      <div className="mt-4 px-4">
        <div className=" text-22 font-bold leading-8">
          <span className="text-appblue ">{state.phoneNumber}</span>로 전송받은
          <br/>인증번호를 입력해 주세요.
        </div>

        <div className="mt-6 text-15 mb-8">
          인증 번호를 받지 못한 경우 스팸함을 확인하거나 매장 담당자에게
          연락해주세요.
        </div>
        <InputCode length={CODE_LENGTH} onChange={inputCodeOnChange} />
        <div className="mt-6 flex justify-between items-center">
          <div
            className={`${isWarning ? "text-red" : "text-darkblue"} font-bold`}
          >
            남은 시간 {timeLeft.minutes.toString().padStart(2, "0")}:
            {timeLeft.seconds.toString().padStart(2, "0")}
          </div>
          <div>
            <OutlineSmallBtn
              className="flex items-center justify-center"
              onClick={() => {
                if (!resendLoading) resendCode();
              }}
            >
              <Spinner isLoading={resendLoading} color="#111B48" />
              인증문자 재발송
            </OutlineSmallBtn>
          </div>
        </div>

        <div className="px-5 absolute w-full bottom-2 left-0">
          <SolidBlueBtn
            disabled={
              codeRef.current?.length < CODE_LENGTH || isEnded || loading
            }
            className="w-full mt-5 flex items-center justify-center"
            type="submit"
            onClick={onSubmit}
          >
            <Spinner isLoading={loading} />
            인증
          </SolidBlueBtn>
        </div>
      </div>

      <AppAlert
        title="인증코드가 올바르지 않습니다."
        desc="번호를 다시 한번 확인해 주세요."
        open={openIncorrect}
        onOk={() => setOpenIncorrect(false)}
        okText="확인"
        type="error"
      />
    </div>
  );
};
export default VerifyCode;
